// These are the pages you can go to.
// They are all wrapped in the App component, which should contain the navbar etc
// See http://blog.mxstbr.com/2016/01/react-apps-with-pages for more information
// about the code splitting business
import { browserHistory, formatPattern } from 'react-router';
import { compact } from 'lodash';

import getReducerInjectors from '@lib/utils/reducerInjectors';
import getSagaInjectors from '@lib/utils/sagaInjectors';
import storage from '@lib/services/storageService';
import Examples from 'examples/Examples';
import { sendLog, LoggerStatusEnum } from 'ui-model';
import { ProvisioningEnum } from '@lib/resources/types';
import isOnEmbedPage from '@lib/utils/isOnEmbedPage';

import App from 'containers/App';
import { selectPermissions } from 'selectors';
import { hasAccessToProduct, hasAccessToPathname } from 'utils/permissions';

export const regexRoutesNoAuthRequired = /^\/embed-error|^\/embed\/cloak|^\/zendesk\/login|^\/login|^\/forgot-password|^\/demo-setup|^\/reset-password|^\/signup|^\/scheduler|^\/practice|^\/unsubscribe|^\/groups|^\/join|^\/telehealth/i;

const errorLoading = (err) => {
  sendLog('Error on routes.js - Dynamic page loading failed', err, LoggerStatusEnum.error);

  if (isOnEmbedPage()) {
    browserHistory.push('/embed-error');
  } else {
    browserHistory.push('/notfound');
  }
};

const loadModule = (cb) => (componentModule) => {
  cb(null, componentModule.default);
};

export function hasAccessDevPlayground() {
  return process.env.NODE_ENV !== 'production' || /localhost|lumahealthdev|designtest/.test(window.location.host);
}

const redirectFromOldSettings = (path, replace) => {
  const replaceStructure = {
    '/settings': '/v2/settings/home',
    '/settings/general/account': '/v2/settings/home/my-account',
    '/settings/general/notificationsSettings': '/v2/settings/home/my-account',
    '/settings/general/clinic': '/v2/settings/features/general',
    '/settings/general/billing': '/v2/settings/home/main-account',
    '/settings/resources': '/v2/settings/practice-resources/providers',
    '/settings/features': '/v2/settings/features/reminder',
    '/settings/features/appointment': '/v2/settings/features/reminder',
    '/settings/features/cancellation': '/v2/settings/features/waitlist',
    '/settings/features/feedback': '/v2/settings/features/feedback',
    '/settings/features/referrals': '/v2/settings/features/inbound-referral',
    '/settings/features/scheduler': '/v2/settings/features/scheduler',
    '/settings/features/forms': '/v2/settings/utilities/form-builder',
    '/settings/features/hub': '/v2/settings/features/communication',
    '/settings/templates': '/v2/settings/utilities/message-templates/appointments',
    '/settings/library/broadcast': '/v2/settings/utilities/message-library/broadcast',
    '/settings/library/chat': '/v2/settings/utilities/message-library/chat',
    '/settings/userManagement': '/v2/settings/security-center/users',
    '/settings/actions': '/v2/settings/utilities/custom-actions',
    '/settings/styling': '/v2/settings/utilities/branding',
    '/settings/patientBilling': '/v2/settings/utilities/patient-billing/items',
    '/settings/patientBilling/payouts': '/v2/settings/utilities/patient-billing/payouts',
    '/settings/patientBilling/account-management': '/v2/settings/utilities/patient-billing/account-management',
  };

  const cleanPath = path[path.length - 1] === '/' ? path.slice(0, -1) : path;

  if (replaceStructure[cleanPath]) {
    return replace({ pathname: replaceStructure[cleanPath] });
  }
};

/**
 * Redirects to a route using the same parameters as the current route.
 *
 * @param {string} url
 * @returns {function} Redirect function
 */
function redirectURL(url) {
  return ({ params }) => browserHistory.push(formatPattern(url, params));
}

export function createRoutes(store) {
  // Create reusable async injectors using getAsyncInjectors factory
  const reducerInjectors = getReducerInjectors(store);
  const sagaInjectors = getSagaInjectors(store);

  const onEnter = (nextState, replace) => {
    const {
      location: { pathname, query },
    } = nextState;

    // For HubJr mobile friendly page
    // https://lumahealth.clickup.com/t/864dp0p84
    const mainScreenMobile = sessionStorage.getItem('mainScreenMobile');
    const mainScreenMobilePathname = `/${mainScreenMobile}`;
    if (mainScreenMobile && pathname !== mainScreenMobilePathname && !regexRoutesNoAuthRequired.test(pathname)) {
      const token = query?.token;
      if (token) storage.setUser({ token });

      return replace({ pathname: mainScreenMobilePathname });
    }

    // check permissions
    const state = store.getState();
    const permissions = selectPermissions(state);

    // need a different check for referrals because either inbound and outbound referrals uses the same endpoint
    const hasReferralsAccess =
      /\/referrals?/.test(pathname) &&
      hasAccessToProduct(permissions, [ProvisioningEnum.Referral, ProvisioningEnum.OutboundReferral]);
    const hasFormBuilderAccess =
      /\/settings\/features\/forms/.test(pathname) &&
      hasAccessToProduct(permissions, [
        ProvisioningEnum.IntakeForm,
        ProvisioningEnum.PrequalForm,
        ProvisioningEnum.Feedback,
      ]);
    const hasSchedulerAccess =
      /\/settings\/features\/scheduler/.test(pathname) &&
      hasAccessToProduct(permissions, [ProvisioningEnum.Scheduler, ProvisioningEnum.PrequalForm]);
    const hasReportsApptTabAccess =
      /\/appointments/.test(pathname) &&
      hasAccessToProduct(permissions, [
        ProvisioningEnum.Reminder,
        ProvisioningEnum.Waitlist,
        ProvisioningEnum.Scheduler,
        ProvisioningEnum.IntakeForm,
        ProvisioningEnum.PrequalForm,
        ProvisioningEnum.Telehealth,
      ]);

    const hasOffersAccess =
      /\/offers/.test(pathname) &&
      hasAccessToProduct(permissions, [ProvisioningEnum.Waitlist, ProvisioningEnum.Scheduler]);
    const hasReminderTabAccess =
      /\/reminders/.test(pathname) &&
      hasAccessToProduct(permissions, [
        ProvisioningEnum.Reminder,
        ProvisioningEnum.Feedback,
        ProvisioningEnum.Followup,
        ProvisioningEnum.Referral,
        ProvisioningEnum.OutboundReferral,
        ProvisioningEnum.Recall,
        ProvisioningEnum.IntakeForm,
        ProvisioningEnum.PrequalForm,
        ProvisioningEnum.Telehealth,
      ]);

    /**
     * TODO: remove this and add provisioning
     */
    const hasInsightsCenterAccess = /\/insights-center/.test(pathname);

    const hasLumaPaySummaryAccess =
      /lumapay\/summary/i.test(pathname) &&
      hasAccessToProduct(permissions, [ProvisioningEnum.LumaPay, ProvisioningEnum.InsuranceVerification]);

    const hasAccessToCopayDetermination =
      /copay-determination/i.test(pathname) && hasAccessToProduct(permissions, ProvisioningEnum.InsuranceVerification);

    if (!/v2\/settings/.test(pathname) && /\/settings/.test(pathname)) {
      redirectFromOldSettings(pathname, replace);
    }

    // prevent access-denied redirect on patient chat log page
    if (
      !/v2\/settings/.test(pathname) && // Settings V2 manages access within its scope
      permissions &&
      !/(\/chat|\/journey)/.test(pathname) &&
      !hasAccessToPathname(permissions, pathname) &&
      !hasReferralsAccess &&
      !hasFormBuilderAccess &&
      !hasSchedulerAccess &&
      !hasReportsApptTabAccess &&
      !hasReminderTabAccess &&
      !hasLumaPaySummaryAccess &&
      !hasOffersAccess &&
      !hasInsightsCenterAccess &&
      !hasAccessToCopayDetermination
    ) {
      return replace({ pathname: '/access-denied' });
    }

    if (!/v2\/settings/.test(pathname) && /\/settings/.test(pathname)) {
      redirectFromOldSettings(pathname, replace);
    }
  };

  const getComponentPatient = (_, cb) => {
    const importModules = Promise.all([
      import('containers/DialogPatientMerge/reducer'),
      import('containers/DialogPatientMerge/sagas'),
      import('containers/PatientPage/reducer'),
      import('containers/PatientPage/sagas'),
      import('screens/nexty-next/Patient'),
    ]);

    const renderRoute = loadModule(cb);

    importModules.then(([reducerDialogPatientMerge, sagasDialogPatientMerge, reducer, sagas, component]) => {
      reducerInjectors.injectReducer('dialogPatientMerge', reducerDialogPatientMerge.default);
      sagaInjectors.injectSaga('containers/DialogPatientMerge/sagas', { saga: sagasDialogPatientMerge.default });
      reducerInjectors.injectReducer('patientPage', reducer.default);
      sagaInjectors.injectSaga('containers/PatientPage/sagas', { saga: sagas.default });
      renderRoute(component);
    });

    importModules.catch(errorLoading);
  };

  const getComponentPatientEmbed = (_, cb) => {
    const importModules = Promise.all([
      import('embeds/components/DialogPatientMerge/reducer'),
      import('embeds/components/DialogPatientMerge/sagas'),
      import('embeds/PatientPage/reducer'),
      import('embeds/PatientPage/sagas'),
      import('embeds/PatientPage/EmbedAddPatientPage'),
    ]);

    const renderRoute = loadModule(cb);

    importModules.then(([reducerDialogPatientMerge, sagasDialogPatientMerge, reducer, sagas, component]) => {
      reducerInjectors.injectReducer('dialogPatientMerge', reducerDialogPatientMerge.default);
      sagaInjectors.injectSaga('embeds/components/DialogPatientMerge/sagas', { saga: sagasDialogPatientMerge.default });
      reducerInjectors.injectReducer('patientPage', reducer.default);
      sagaInjectors.injectSaga('embeds/PatientPage/sagas', { saga: sagas.default });
      renderRoute(component);
    });

    importModules.catch(errorLoading);
  };

  const getComponentRedirectWaitlist = (_, cb) => {
    const importModules = Promise.all([
      import('containers/WaitlistPage/reducer'),
      import('containers/WaitlistPage/sagas'),
      import('containers/WaitlistPage/RedirectWaitlist'),
    ]);

    const renderRoute = loadModule(cb);

    importModules.then(([reducer, sagas, component]) => {
      reducerInjectors.injectReducer('waitlistPage', reducer.default);
      sagaInjectors.injectSaga('containers/WaitlistPage/sagas', { saga: sagas.default });
      renderRoute(component);
    });

    importModules.catch(errorLoading);
  };

  const getComponentRedirectEmbedWaitlist = (_, cb) => {
    const importModules = Promise.all([
      import('embeds/Waitlists/WaitlistsEditPage/reducer'),
      import('embeds/Waitlists/WaitlistsEditPage/sagas'),
      import('embeds/Waitlists/WaitlistsEditPage/RedirectWaitlist'),
    ]);

    const renderRoute = loadModule(cb);

    importModules.then(([reducer, sagas, component]) => {
      reducerInjectors.injectReducer('waitlistPage', reducer.default);
      sagaInjectors.injectSaga('embeds/Waitlists/WaitlistsEditPage/sagas', { saga: sagas.default });
      renderRoute(component);
    });

    importModules.catch(errorLoading);
  };

  const embedRoutes = [
    {
      path: '/embed/cloak/:hash',
      name: 'embed-cloak',
      getComponent(_, cb) {
        import('embeds/cloak')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: '/embed/hub',
      onEnter,
      getComponent(_, cb) {
        const importModules = Promise.all([
          import('embeds/containers/DialogSavedTemplatesSelector/reducer'),
          import('embeds/containers/DialogSavedTemplatesSelector/sagas'),
          import('embeds/sagas/sagaEmbedRegisterSocket'),
          import('embeds/HubScreen'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(
          ([
            reducerDialogSavedTemplatesSelector,
            sagasDialogSavedTemplatesSelector,
            sagaEmbedRegisterSocket,
            component,
          ]) => {
            reducerInjectors.injectReducer('dialogSavedTemplatesSelector', reducerDialogSavedTemplatesSelector.default);
            sagaInjectors.injectSaga('embeds/containers/DialogSavedTemplatesSelector/sagas', {
              saga: sagasDialogSavedTemplatesSelector.default,
            });
            sagaInjectors.injectSaga('embeds/sagas/sagaEmbedRegisterSocket', {
              saga: sagaEmbedRegisterSocket.default,
            });
            renderRoute(component);
          },
        );

        importModules.catch(errorLoading);
      },
    },
    {
      path: '/embed/broadcasts',
      name: 'embed-broadcast',
      getComponent(_, cb) {
        import('screens/nexty-next/BroadcastList')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/broadcasts/:id(/:action)',
      name: 'embed-broadcast-edit',
      getComponent(_, cb) {
        import('embeds/broadcasts/BroadcastEditScreen')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/messaging-activity/:tab',
      getComponent(_, cb) {
        import('embeds/MessagingActivity')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/uploadcenter',
      name: 'embed-uploadcenter',
      onEnter,
      getComponent(_, cb) {
        const importModules = Promise.all([
          import('embeds/UploadCenterPage/reducer'),
          import('embeds/UploadCenterPage/sagas'),
          import('embeds/UploadCenterPage'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(([reducer, sagas, component]) => {
          reducerInjectors.injectReducer('uploadCenterPage', reducer.default);
          sagaInjectors.injectSaga('embeds/UploadCenterPage/sagas', { saga: sagas.default });
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    },
    {
      path: 'embed/referrals',
      name: 'embed-referrals',
      onEnter,
      getComponent(_, cb) {
        const importModules = Promise.all([
          import('embeds/ReferralsPage/reducer'),
          import('embeds/ReferralsPage/sagas'),
          import('embeds/ReferralsPage/ReferralsQueryWrapper'),
        ]);
        const renderRoute = loadModule(cb);

        importModules.then(([reducer, sagas, component]) => {
          reducerInjectors.injectReducer('referralsPage', reducer.default);
          sagaInjectors.injectSaga('embeds/ReferralsPage/sagas', { saga: sagas.default });
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    },
    {
      path: 'embed/referral/:id',
      name: 'embed-referralPage',
      onEnter,
      getComponent(_, cb) {
        const importModules = Promise.all([
          import('embeds/ReferralPage/reducer'),
          import('embeds/ReferralPage/sagas'),
          import('embeds/ReferralPage/EmbedReferralPage'),
          import('embeds/components/DialogNewExternalProvider/reducer'),
          import('embeds/components/DialogNewExternalProvider/sagas'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(
          ([reducer, sagas, component, reducerDialogNewExternalProvider, sagasDialogNewExternalProvider]) => {
            reducerInjectors.injectReducer('referralPage', reducer.default);
            sagaInjectors.injectSaga('embeds/ReferralPage/sagas', { saga: sagas.default });
            reducerInjectors.injectReducer('dialogNewExternalProvider', reducerDialogNewExternalProvider.default);
            sagaInjectors.injectSaga('embeds/components/DialogNewExternalProvider/sagas', {
              saga: sagasDialogNewExternalProvider.default,
            });
            renderRoute(component);
          },
        );

        importModules.catch(errorLoading);
      },
    },
    {
      path: '/embed/referral/outbound/:id',
      name: 'referralOutbound',
      onEnter,
      getComponent(_, cb) {
        const importModules = Promise.all([
          import('embeds/ReferralOutboundPage/reducer'),
          import('embeds/ReferralOutboundPage/sagas'),
          import('embeds/ReferralOutboundPage/EmbedReferralOutboundPage'),
          import('embeds/components/DialogNewExternalProvider/reducer'),
          import('embeds/components/DialogNewExternalProvider/sagas'),
        ]);
        const renderRoute = loadModule(cb);

        importModules.then(
          ([reducer, sagas, component, reducerDialogNewExternalProvider, sagasDialogNewExternalProvider]) => {
            reducerInjectors.injectReducer('referralOutboundPage', reducer.default);
            sagaInjectors.injectSaga('embeds/ReferralOutboundPage/sagas', { saga: sagas.default });
            reducerInjectors.injectReducer('dialogNewExternalProvider', reducerDialogNewExternalProvider.default);
            sagaInjectors.injectSaga('embeds/components/DialogNewExternalProvider/sagas', {
              saga: sagasDialogNewExternalProvider.default,
            });
            renderRoute(component);
          },
        );

        importModules.catch(errorLoading);
      },
    },
    {
      path: 'embed/referrals/upload',
      name: 'embed-upload-referrals',
      onEnter,
      getComponent(_, cb) {
        const importModules = Promise.all([
          import('embeds/UploadReferrals/reducer'),
          import('embeds/UploadReferrals/sagas'),
          import('embeds/UploadReferrals'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(([reducer, sagas, component]) => {
          reducerInjectors.injectReducer('uploadReferralsPage', reducer.default);
          sagaInjectors.injectSaga('embeds/UploadReferrals/sagas', { saga: sagas.default });
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    },
    {
      path: 'embed/outbound-referrals',
      name: 'embed-outbound-referrals',
      onEnter,
      getComponent(nextState, cb) {
        const importModules = Promise.all([
          import('embeds/ReferralsPage/reducer'),
          import('embeds/ReferralsPage/sagas'),
          import('embeds/ReferralsPage/ReferralsQueryWrapper'),
        ]);
        const renderRoute = loadModule(cb);

        importModules.then(([reducer, sagas, component]) => {
          reducerInjectors.injectReducer('referralsPage', reducer.default);
          sagaInjectors.injectSaga('embeds/ReferralsPage/sagas', { saga: sagas.default });
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    },
    {
      path: 'embed/schedule',
      getComponent(_, cb) {
        import('embeds/Schedule')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/waiting-room',
      getComponent(_, cb) {
        import('embeds/WaitingRoom')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/file-management',
      name: 'embed-file-management',
      getComponent(_, cb) {
        import('embeds/FileManagementScreen')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/waitlists/accept',
      name: 'watilistsAccept',
      getComponent: getComponentRedirectEmbedWaitlist,
    },
    {
      path: 'embed/waitlists/decline',
      name: 'watilistsDecline',
      getComponent: getComponentRedirectEmbedWaitlist,
    },
    {
      path: 'embed/waitlists',
      name: 'embed-waitlists',
      onEnter,
      getComponent(_, cb) {
        const importModules = Promise.all([
          import('embeds/Waitlists/WaitlistsListPage/legacy/reducer'),
          import('embeds/Waitlists/WaitlistsListPage/legacy/sagas'),
          import('embeds/Waitlists/WaitlistsEditPage/reducer'),
          import('embeds/Waitlists/WaitlistsEditPage/sagas'),
          import('embeds/components/DialogSendOffers/reducer'),
          import('embeds/components/DialogSendOffers/sagas'),
          import('embeds/Waitlists/WaitlistsListPage'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(
          ([
            reducer,
            sagas,
            reducerWaitlistPage,
            sagasWaitlistPage,
            reducerDialogSendOffers,
            sagasDialogSendOffers,
            component,
          ]) => {
            reducerInjectors.injectReducer('waitlistsPage', reducer.default);
            sagaInjectors.injectSaga('embeds/Waitlists/WaitlistsListPage/sagas', { saga: sagas.default });
            reducerInjectors.injectReducer('waitlistPage', reducerWaitlistPage.default);
            sagaInjectors.injectSaga('embeds/Waitlists/WaitlistsEditPage/sagas', { saga: sagasWaitlistPage.default });
            reducerInjectors.injectReducer('dialogSendOffer', reducerDialogSendOffers.default);
            sagaInjectors.injectSaga('embeds/components/DialogSendOffers/sagas', {
              saga: sagasDialogSendOffers.default,
            });
            renderRoute(component);
          },
        );

        importModules.catch(errorLoading);
      },
    },
    {
      path: 'embed/waitlists/:id',
      onEnter,
      getComponent(_, cb) {
        const importModules = Promise.all([
          import('embeds/Waitlists/WaitlistsEditPage/reducer'),
          import('embeds/Waitlists/WaitlistsEditPage/sagas'),
          import('embeds/Waitlists/WaitlistsEditPage'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(([reducer, sagas, component]) => {
          reducerInjectors.injectReducer('waitlistPage', reducer.default);
          sagaInjectors.injectSaga('embeds/Waitlists/WaitlistsEditPage/sagas', { saga: sagas.default });
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/home/my-account',
      getComponent(_, cb) {
        Promise.all([
          import('embeds/MissionControl/MyAccount/MyAccountPage'),
          import('embeds/MissionControl/MyAccount/DialogTwoFactorDisable/reducer'),
          import('embeds/MissionControl/MyAccount/DialogTwoFactorDisable/sagas'),
          import('embeds/MissionControl/MyAccount/DialogTwoFactorEnable/reducer'),
          import('embeds/MissionControl/MyAccount/DialogTwoFactorEnable/sagas'),
        ])
          .then(
            ([
              component,
              dialogTwoFactorDisableReducer,
              dialogTwoFactorDisableSagas,
              dialogTwoFactorEnableReducer,
              dialogTwoFactorEnableSagas,
            ]) => {
              reducerInjectors.injectReducer('dialogTwoFactorDisable', dialogTwoFactorDisableReducer.default);
              sagaInjectors.injectSaga('embeds/MissionControl/MyAccount/DialogTwoFactorDisable/sagas', {
                saga: dialogTwoFactorDisableSagas.default,
              });
              reducerInjectors.injectReducer('dialogTwoFactorEnable', dialogTwoFactorEnableReducer.default);
              sagaInjectors.injectSaga('embeds/MissionControl/MyAccount/DialogTwoFactorEnable/sagas', {
                saga: dialogTwoFactorEnableSagas.default,
              });
              loadModule(cb)(component);
            },
          )
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/practice-resources/providers',
      getComponent(_, cb) {
        import('embeds/MissionControl/PracticeResourcesPage/ProviderResourcePage')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/practice-resources/providers/:id',
      getComponent(_, cb) {
        Promise.all([
          import('embeds/components/DialogAvailabilityBlocks/reducer'),
          import(
            // eslint-disable-next-line
            'embeds/MissionControl/PracticeResourcesPage/ProviderResourcePage/ProviderEditPage'
          ),
        ])
          .then(([reducer, component]) => {
            reducerInjectors.injectReducer('dialogAvailabilityBlocks', reducer.default);
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/practice-resources/facilities',
      getComponent(_, cb) {
        import('embeds/MissionControl/PracticeResourcesPage/FacilityResourcePage')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/practice-resources/facilities/:id',
      getComponent(_, cb) {
        import('embeds/MissionControl/PracticeResourcesPage/FacilityResourcePage/FacilityEditPage')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/practice-resources/appointment-types',
      getComponent(_, cb) {
        import('embeds/MissionControl/PracticeResourcesPage/ApptTypeResourcePage')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/practice-resources/appointment-types/:id',
      getComponent(_, cb) {
        Promise.all([
          import('embeds/components/DialogAvailabilityBlocks/reducer'),
          import(
            // eslint-disable-next-line
            'embeds/MissionControl/PracticeResourcesPage/ApptTypeResourcePage/ApptTypeEditPage'
          ),
        ])
          .then(([reducer, component]) => {
            reducerInjectors.injectReducer('dialogAvailabilityBlocks', reducer.default);
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      // NOTE: [line below] 'someRoute(/:subRoute)' is react-router's (v1-v3) syntax for optional sub-routes
      path: 'embed/settings/utilities/message-templates(/:tab)(/:id)',
      getComponent(_, cb) {
        Promise.all([
          import('embeds/MissionControl/Utilities/MessageTemplatesPage/TemplatesPage/reducer.js'),
          import('embeds/MissionControl/Utilities/MessageTemplatesPage/TemplatesPage/sagas.js'),
          import('embeds/MissionControl/Utilities/MessageTemplatesPage'),
        ])
          .then(([reducer, sagas, component]) => {
            reducerInjectors.injectReducer('settingsTemplatesPage', reducer.default);
            sagaInjectors.injectSaga('embeds/MissionControl/Utilities/MessageTemplatesPage/TemplatesPage/sagas', {
              saga: sagas.default,
            });
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/features/:feature',
      getComponent(_, cb) {
        import('embeds/MissionControl/FeaturesSettingsPage')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/home/main-account',
      getComponent(_, cb) {
        import('embeds/MissionControl/MainAccountPage')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/utilities/branding/patient-form',
      getComponent(_, cb) {
        Promise.all([
          import('embeds/MissionControl/Utilities/BrandingPage/reducer.js'),
          import('embeds/MissionControl/Utilities/BrandingPage/sagas.js'),
          import('embeds/MissionControl/Utilities/BrandingPage'),
        ])
          .then(([reducer, sagas, component]) => {
            reducerInjectors.injectReducer('settingsStylingPage', reducer.default);
            sagaInjectors.injectSaga('embeds/MissionControl/Utilities/BrandingPage/sagas', { saga: sagas.default });
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/utilities/patient-billing/vendor',
      getComponent(_, cb) {
        Promise.all([import('embeds/MissionControl/Utilities/PatientBillingPage/components/BillingVendor')])
          .then(([component]) => {
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/utilities/patient-billing/items',
      getComponent(_, cb) {
        Promise.all([import('embeds/MissionControl/Utilities/PatientBillingPage/components/BillingItems')])
          .then(([component]) => {
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/utilities/patient-billing/items/:id',
      getComponent(_, cb) {
        Promise.all([import('embeds/MissionControl/Utilities/PatientBillingPage/components/BillingItems')])
          .then(([component]) => {
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/reports',
      getComponent(_, cb) {
        import('embeds/Reports')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/reports/:tab',
      getComponent(_, cb) {
        import('embeds/Reports')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/form-builder',
      getComponent(_, cb) {
        Promise.all([import('embeds/MissionControl/Utilities/NewFormsListPage')])
          .then(([component]) => {
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      // NOTE: [line below] 'someRoute(/:subRoute)' is react-router's (v1-v3) syntax for optional sub-routes
      path: 'embed/settings/utilities/message-library(/:tab)',
      getComponent(_, cb) {
        Promise.all([
          import('embeds/MissionControl/Utilities/MessageLibraryPage/LibraryPage/reducer.js'),
          import('embeds/MissionControl/Utilities/MessageLibraryPage/LibraryPage/sagas.js'),
          import('embeds/MissionControl/Utilities/MessageLibraryPage'),
        ])
          .then(([reducer, sagas, component]) => {
            reducerInjectors.injectReducer('settingsLibraryPage', reducer.default);
            sagaInjectors.injectSaga('embeds/MissionControl/Utilities/MessageLibraryPage/LibraryPage/sagas', {
              saga: sagas.default,
            });
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/form-builder/:id',
      getComponent(_, cb) {
        Promise.all([import('embeds/MissionControl/Utilities/NewFormBuilderPage')])
          .then(([component]) => {
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      // NOTE: [line below] 'someRoute(/:subRoute)' is react-router's (v1-v3) syntax for optional sub-routes
      path: 'embed/settings/utilities/message-library(/:tab)',
      getComponent(_, cb) {
        Promise.all([
          import('embeds/MissionControl/Utilities/MessageLibraryPage/LibraryPage/reducer.js'),
          import('embeds/MissionControl/Utilities/MessageLibraryPage/LibraryPage/sagas.js'),
          import('embeds/MissionControl/Utilities/MessageLibraryPage'),
        ])
          .then(([reducer, sagas, component]) => {
            reducerInjectors.injectReducer('settingsLibraryPage', reducer.default);
            sagaInjectors.injectSaga('embeds/MissionControl/Utilities/MessageLibraryPage/LibraryPage/sagas', {
              saga: sagas.default,
            });
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/utilities/custom-actions',
      onEnter,
      getComponent(_, cb) {
        const importModules = Promise.all([
          import('embeds/MissionControl/Utilities/CustomActionsPage/SettingsCustomActionsPage/reducer'),
          import('embeds/MissionControl/Utilities/CustomActionsPage/SettingsCustomActionsPage/sagas'),
          import('embeds/MissionControl/Utilities/CustomActionsPage'),
          import('embeds/MissionControl/Utilities/CustomActionsPage/components/DialogCustomActionGroup/reducer'),
          import('embeds/MissionControl/Utilities/CustomActionsPage/components/DialogCustomActionGroup/sagas'),
          import('embeds/MissionControl/Utilities/CustomActionsPage/components/DialogDeleteCustomAction/reducer'),
          import('embeds/MissionControl/Utilities/CustomActionsPage/components/DialogDeleteCustomAction/sagas'),
          import('embeds/MissionControl/Utilities/CustomActionsPage/components/DialogDeleteCustomActionGroup/reducer'),
          import('embeds/MissionControl/Utilities/CustomActionsPage/components/DialogDeleteCustomActionGroup/sagas'),
          import('embeds/MissionControl/Utilities/CustomActionsPage/components/DialogMoveCustomAction/reducer'),
          import('embeds/MissionControl/Utilities/CustomActionsPage/components/DialogMoveCustomAction/sagas'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(
          ([
            reducer,
            sagas,
            component,
            dialogCustomActionGroupReducer,
            dialogCustomActionGroupSagas,
            dialogDeleteCustomActionReducer,
            dialogDeleteCustomActionSagas,
            dialogDeleteCustomActionGroupReducer,
            dialogDeleteCustomActionGroupSagas,
            dialogMoveCustomActionReducer,
            dialogMoveCustomActionSagas,
          ]) => {
            reducerInjectors.injectReducer('settingsCustomActionsPage', reducer.default);
            sagaInjectors.injectSaga('containers/CustomActionsPage/sagas', { saga: sagas.default });
            reducerInjectors.injectReducer('dialogCustomActionGroup', dialogCustomActionGroupReducer.default);
            sagaInjectors.injectSaga('containers/DialogCustomActionGroup/sagas', {
              saga: dialogCustomActionGroupSagas.default,
            });
            reducerInjectors.injectReducer('dialogDeleteCustomAction', dialogDeleteCustomActionReducer.default);
            sagaInjectors.injectSaga('containers/DialogDeleteCustomAction/sagas', {
              saga: dialogDeleteCustomActionSagas.default,
            });
            reducerInjectors.injectReducer(
              'dialogDeleteCustomActionGroup',
              dialogDeleteCustomActionGroupReducer.default,
            );
            sagaInjectors.injectSaga('DialogDeleteCustomActionGroup', {
              saga: dialogDeleteCustomActionGroupSagas.default,
            });
            reducerInjectors.injectReducer('dialogMoveCustomAction', dialogMoveCustomActionReducer.default);
            sagaInjectors.injectSaga('containers/DialogMoveCustomAction/sagas', {
              saga: dialogMoveCustomActionSagas.default,
            });
            renderRoute(component);
          },
        );

        importModules.catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/utilities/custom-actions/appointment/:id',
      name: 'settingsCustomActionNewAppointmentPage',
      onEnter,
      getComponent(_, cb) {
        Promise.all([
          import(
            'embeds/MissionControl/Utilities/CustomActionsPage/NewAppointmentPage/SettingsCustomActionNewAppointmentPage/reducer'
          ),
          import(
            'embeds/MissionControl/Utilities/CustomActionsPage/NewAppointmentPage/SettingsCustomActionNewAppointmentPage/sagas'
          ),
          import('embeds/MissionControl/Utilities/CustomActionsPage/NewAppointmentPage'),
        ])
          .then(([reducer, sagas, component]) => {
            reducerInjectors.injectReducer('settingsCustomActionNewAppointmentPage', reducer.default);
            sagaInjectors.injectSaga(
              'embeds/MissionControl/Utilities/CustomActionsPage/NewAppointmentPage/SettingsCustomActionNewAppointmentPage/sagas',
              {
                saga: sagas.default,
              },
            );
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/utilities/custom-actions/diagnostic/:id',
      name: 'settingsCustomActionNewDiagnosticPage',
      onEnter,
      getComponent(_, cb) {
        Promise.all([
          import(
            'embeds/MissionControl/Utilities/CustomActionsPage/NewDiagnosticPage/SettingsCustomActionNewDiagnosticPage/reducer'
          ),
          import(
            'embeds/MissionControl/Utilities/CustomActionsPage/NewDiagnosticPage/SettingsCustomActionNewDiagnosticPage/sagas'
          ),
          import('embeds/MissionControl/Utilities/CustomActionsPage/NewDiagnosticPage'),
        ])
          .then(([reducer, sagas, component]) => {
            reducerInjectors.injectReducer('settingsCustomActionNewDiagnosticPage', reducer.default);
            sagaInjectors.injectSaga(
              'embeds/MissionControl/Utilities/CustomActionsPage/NewDiagnosticPage/SettingsCustomActionNewDiagnosticPage/sagas',
              {
                saga: sagas.default,
              },
            );
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/utilities/custom-actions/reminder/:id',
      name: 'settingsCustomActionNewExtraReminderPage',
      onEnter,
      getComponent(_, cb) {
        Promise.all([
          import(
            'embeds/MissionControl/Utilities/CustomActionsPage/NewExtraReminderPage/SettingsCustomActionNewExtraReminderPage/reducer'
          ),
          import(
            'embeds/MissionControl/Utilities/CustomActionsPage/NewExtraReminderPage/SettingsCustomActionNewExtraReminderPage/sagas'
          ),
          import('embeds/MissionControl/Utilities/CustomActionsPage/NewExtraReminderPage'),
        ])
          .then(([reducer, sagas, component]) => {
            reducerInjectors.injectReducer('settingsCustomActionNewExtraReminderPage', reducer.default);
            sagaInjectors.injectSaga(
              'embeds/MissionControl/Utilities/CustomActionsPage/NewExtraReminderPage/SettingsCustomActionNewExtraReminderPage/sagas',
              {
                saga: sagas.default,
              },
            );
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/utilities/custom-actions/procedure/:id',
      name: 'settingsCustomActionNewProcedurePage',
      onEnter,
      getComponent(_, cb) {
        Promise.all([
          import(
            'embeds/MissionControl/Utilities/CustomActionsPage/NewProcedurePage/SettingsCustomActionNewProcedurePage/reducer'
          ),
          import(
            'embeds/MissionControl/Utilities/CustomActionsPage/NewProcedurePage/SettingsCustomActionNewProcedurePage/sagas'
          ),
          import('embeds/MissionControl/Utilities/CustomActionsPage/NewProcedurePage'),
        ])
          .then(([reducer, sagas, component]) => {
            reducerInjectors.injectReducer('settingsCustomActionNewProcedurePage', reducer.default);
            sagaInjectors.injectSaga(
              'embeds/MissionControl/Utilities/CustomActionsPage/NewProcedurePage/SettingsCustomActionNewProcedurePage/sagas',
              {
                saga: sagas.default,
              },
            );
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/settings/utilities/custom-actions/recall/:id',
      name: 'settingsCustomActionNewRecallPage',
      onEnter,
      getComponent(_, cb) {
        Promise.all([
          import(
            'embeds/MissionControl/Utilities/CustomActionsPage/NewRecallPage/SettingsCustomActionNewRecallPage/reducer'
          ),
          import(
            'embeds/MissionControl/Utilities/CustomActionsPage/NewRecallPage/SettingsCustomActionNewRecallPage/sagas'
          ),
          import('embeds/MissionControl/Utilities/CustomActionsPage/NewRecallPage'),
        ])
          .then(([reducer, sagas, component]) => {
            reducerInjectors.injectReducer('settingsCustomActionNewRecallPage', reducer.default);
            sagaInjectors.injectSaga(
              'embeds/MissionControl/CustomActionsPage/NewRecallPage/SettingsCustomActionNewRecallPage/sagas',
              {
                saga: sagas.default,
              },
            );
            loadModule(cb)(component);
          })
          .catch(errorLoading);
      },
    },
    {
      path: 'embed/patients/add',
      onEnter,
      getComponent: getComponentPatientEmbed,
    },
    {
      path: 'embed/patients/edit/:id',
      name: 'patientPage',
      onEnter,
      getComponent(_, cb) {
        const importModules = Promise.all([
          import('embeds/components/DialogPatientMerge/reducer'),
          import('embeds/components/DialogPatientMerge/sagas'),
          import('embeds/components/DialogSavedTemplatesSelector/reducer'),
          import('embeds/components/DialogSavedTemplatesSelector/sagas'),
          import('embeds/PatientPage/reducer'),
          import('embeds/PatientPage/sagas'),
          import('embeds/PatientPage/EmbedEditPatientPage'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(
          ([
            reducerDialogPatientMerge,
            sagasDialogPatientMerge,
            reducerDialogSavedTemplatesSelector,
            sagasDialogSavedTemplatesSelector,
            reducer,
            sagas,
            component,
          ]) => {
            reducerInjectors.injectReducer('dialogPatientMerge', reducerDialogPatientMerge.default);
            sagaInjectors.injectSaga('embeds/components/DialogPatientMerge/sagas', {
              saga: sagasDialogPatientMerge.default,
            });
            reducerInjectors.injectReducer('dialogSavedTemplatesSelector', reducerDialogSavedTemplatesSelector.default);
            sagaInjectors.injectSaga('embeds/components/DialogSavedTemplatesSelector/sagas', {
              saga: sagasDialogSavedTemplatesSelector.default,
            });
            reducerInjectors.injectReducer('patientPage', reducer.default);
            sagaInjectors.injectSaga('embeds/PatientPage/sagas', { saga: sagas.default });
            renderRoute(component);
          },
        );

        importModules.catch(errorLoading);
      },
    },
  ];

  const internalRoutes = [
    {
      component: Examples,
      childRoutes: [
        {
          path: '/examples/legacy',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('examples/ExampleLegacy/reducer'),
              import('examples/ExampleLegacy/sagas'),
              import('examples/ExampleLegacy'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('examples/ExampleLegacy', reducer.default);
              sagaInjectors.injectSaga('examples/ExampleLegacy', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/examples/patients',
          getComponent(_, cb) {
            import('examples/ExamplePatients')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/examples/patients/:id',
          getComponent(_, cb) {
            import('examples/ExamplePatient')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/examples/activities',
          getComponent(_, cb) {
            import('examples/ExampleActivities')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/examples/offers',
          getComponent(_, cb) {
            import('examples/ExampleOffers')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/examples/waitlists',
          getComponent(_, cb) {
            import('examples/ExampleWaitlists')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/examples/appointments',
          getComponent(_, cb) {
            import('examples/ExampleAppointments')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/examples/chatActivities',
          getComponent(_, cb) {
            import('examples/ExampleChatActivities')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
      ],
    },
    {
      component: App,
      childRoutes: compact([
        ...embedRoutes,
        {
          path: '/telehealth/:id/audit',
          getComponent(_, cb) {
            import('screens/TelehealthAuditScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/telehealth',
          getComponent(_, cb) {
            import('screens/TelehealthScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/backoffice',
          getComponent(_, cb) {
            import('screens/nexty-next/Backoffice/Home')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/v2/telehealth',
          getComponent(_, cb) {
            import('screens/TelehealthScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/telehealth',
          getComponent(_, cb) {
            import('screens/TelehealthScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/waitlists/accept',
          name: 'watilistsAccept',
          getComponent: getComponentRedirectWaitlist,
        },
        {
          path: '/waitlists/decline',
          name: 'watilistsDecline',
          getComponent: getComponentRedirectWaitlist,
        },
        {
          path: '/v2/formBuilder',
          getComponent(_, cb) {
            import('screens/FormBuilderPageV2Screen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/v2/formBuilder/:id',
          getComponent(_, cb) {
            import('screens/FormBuilderPageV2Screen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          name: 'copayDetermination',
          path: '/copay-determination/:id',
          getComponent(_, cb) {
            import('screens/CopayDetermination')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          name: 'copayDeterminationDetails',
          path: '/copay-determination/details/:id',
          getComponent(_, cb) {
            import('screens/CopayDetermination/CopayDetails')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/schedule',
          getComponent(_, cb) {
            import('screens/nexty-next/ScheduleView')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/scheduler-assist',
          getComponent(_, cb) {
            import('screens/nexty-next/SchedulerAssist')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/forgot-password',
          name: 'forgotPasswordPage',
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/ForgotPasswordPage/reducer'),
              import('containers/ForgotPasswordPage/sagas'),
              import('containers/ForgotPasswordPage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('forgotPasswordPage', reducer.default);
              sagaInjectors.injectSaga('containers/ForgotPasswordPage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/unsubscribe/:token',
          name: 'unsubscribePage',
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/UnsubscribePage/reducer'),
              import('containers/UnsubscribePage/sagas'),
              import('containers/UnsubscribePage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('unsubscribePage', reducer.default);
              sagaInjectors.injectSaga('containers/UnsubscribePage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/unsubscribe/:messageKey/:settingId/:token',
          name: 'unsubscribePage',
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/UnsubscribePage/reducer'),
              import('containers/UnsubscribePage/sagas'),
              import('containers/UnsubscribePage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('unsubscribePage', reducer.default);
              sagaInjectors.injectSaga('containers/UnsubscribePage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/reset-password/:token',
          name: 'resetPasswordPage',
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/ResetPasswordPage/reducer'),
              import('containers/ResetPasswordPage/sagas'),
              import('containers/ResetPasswordPage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('resetPasswordPage', reducer.default);
              sagaInjectors.injectSaga('containers/ResetPasswordPage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/signup/:id/:token',
          name: 'signupTokenPage',
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/SignupPage/reducer'),
              import('containers/SignupPage/sagas'),
              import('containers/SignupPage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('signupPage', reducer.default);
              sagaInjectors.injectSaga('containers/SignupPage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/signup',
          name: 'signupPage',
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/SignupPage/reducer'),
              import('containers/SignupPage/sagas'),
              import('containers/SignupPage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('signupPage', reducer.default);
              sagaInjectors.injectSaga('containers/SignupPage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/join/:groupToken/:token',
          name: 'invitedUserSignupPage',
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/InvitedUserSignupPage/reducer'),
              import('containers/InvitedUserSignupPage/sagas'),
              import('containers/InvitedUserSignupPage'),
            ]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('invitedUserSignupPage', reducer.default);
              sagaInjectors.injectSaga('containers/InvitedUserSignupPage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/demo-setup',
          name: 'demoSetupPage',
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/DemoSetupPage/reducer'),
              import('containers/DemoSetupPage/sagas'),
              import('containers/DemoSetupPage'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('demoSetupPage', reducer.default);
              sagaInjectors.injectSaga('containers/DemoSetupPage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/login',
          name: 'login',
          getComponent(_, cb) {
            const importModules = Promise.all([import('containers/LoginPage/reducer'), import('containers/LoginPage')]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, component]) => {
              storage.removeAuth();
              reducerInjectors.injectReducer('loginPage', reducer.default);
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: 'zendesk/login',
          name: 'login',
          getComponent(_, cb) {
            const importModules = Promise.all([import('containers/LoginPage/reducer'), import('containers/LoginPage')]);
            const renderRoute = loadModule(cb);
            importModules.then(([reducer, component]) => {
              storage.removeAuth();
              reducerInjectors.injectReducer('loginPage', reducer.default);
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/scheduler/:id',
          skipHeader: true,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/Scheduler/reducer'),
              import('containers/Scheduler/sagas'),
              import('containers/Scheduler/'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('scheduler', reducer.default);
              sagaInjectors.injectSaga('containers/Scheduler/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/practice/:name/:id',
          skipHeader: true,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/ExternalReferralPatientPage/reducer'),
              import('containers/ExternalReferralPatientPage/sagas'),
              import('containers/ExternalReferralPatientPage'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('externalReferralPatientPage', reducer.default);
              sagaInjectors.injectSaga('containers/ExternalReferralPatientPage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/',
          name: 'home',
          onEnter,
          getComponent(_, cb) {
            import('screens/nexty-next/Home')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/overview',
          name: 'insights-center',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/Home')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/patient-journey',
          name: 'insights-center/patient-journey',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/PatientJourneyList')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/patient-journey/types',
          name: 'insights-center/patient-journey/types',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/PatientJourneyTypes')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/patient-journey/events',
          name: 'insights-center/patient-journey/events',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/PatientJourneyEventsList')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/patient-journey/:id',
          name: 'insights-center/patient-journey/:id',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/PatientJourneyBuilder')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/featured-reports/messaging-activity(/:subTab)',
          name: 'insights-center/featured-reports/messaging-activity(/:subTab)',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/MessagingActivity')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/featured-reports(/:tab)',
          name: 'insights-center/featured-reports(/:tab)',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/FeaturedReports')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/custom-reports(/:tab)',
          name: 'insights-center/custom-reports(/:tab)',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/FeaturedReports')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/executive-summary',
          name: 'insights-center/executive-summary',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/ExecutiveSummary')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/provider-utilization(/:tab)',
          name: 'insights-center/provider-utilization(/:tab)',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/ProviderUtilization')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/provider-utilization/true-time(/:id)',
          name: 'insights-center/provider-utilization/true-time(/:id)',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/ProviderUtilization')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/recommendations',
          name: '/insights-center/recommendations',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/Recommendations')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/monitoring(/navigator)',
          name: 'insights-center/monitoring(/navigator)',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/MonitoringSparkTable')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/monitoring/hub-activity',
          name: 'insights-center/monitoring/hub-activity',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/MonitoringSparkTable')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/monitoring/navigator/:id',
          name: 'insights-center/monitoring/navigator/:id',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/MonitoringSparkDetails')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/insights-center/monitoring/messaging',
          name: 'insights-center/monitoring/messaging',
          onEnter,
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/MessagingActivity')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/patients',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/PatientsPage/reducer'),
              import('containers/PatientsPage/sagas'),
              import('containers/DialogPatientMerge/reducer'),
              import('containers/DialogPatientMerge/sagas'),
              import('containers/Patients/reducer'),
              import('containers/Patients/sagas'),
              import('containers/PatientsPage'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(
              ([
                reducer,
                sagas,
                reducerDialogPatientMerge,
                sagasDialogPatientMerge,
                reducerPatients,
                sagasPatients,
                component,
              ]) => {
                reducerInjectors.injectReducer('patientsPage', reducer.default);
                sagaInjectors.injectSaga('containers/PatientsPage/sagas', { saga: sagas.default });
                reducerInjectors.injectReducer('dialogPatientMerge', reducerDialogPatientMerge.default);
                sagaInjectors.injectSaga('containers/DialogPatientMerge/sagas', {
                  saga: sagasDialogPatientMerge.default,
                });
                reducerInjectors.injectReducer('patients', reducerPatients.default);
                sagaInjectors.injectSaga('containers/Patients/sagas', { saga: sagasPatients.default });
                renderRoute(component);
              },
            );

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/patients/:id/insurances/:insuranceId',
          name: 'PatientInsuranceCardPage',
          onEnter,
          getComponent(_, cb) {
            import('screens/nexty-next/PatientInsuranceCardPage.tsx')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/patients/:id',
          onEnter,
          getComponent: getComponentPatient,
        },
        {
          path: '/patients/:id/scheduler',
          getComponent(_, cb) {
            import('screens/PatientSchedulerScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/patients/:id/scheduler/availabilities',
          getComponent(_, cb) {
            import('screens/AvailabilitiesScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/patients/:id/book',
          getComponent(_, cb) {
            import('screens/BookScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/patients/:id/:tab',
          name: 'patientPage',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/DialogPatientMerge/reducer'),
              import('containers/DialogPatientMerge/sagas'),
              import('containers/DialogSavedTemplatesSelector/reducer'),
              import('containers/DialogSavedTemplatesSelector/sagas'),
              import('containers/PatientPage/reducer'),
              import('containers/PatientPage/sagas'),
              import('screens/nexty-next/Patient'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(
              ([
                reducerDialogPatientMerge,
                sagasDialogPatientMerge,
                reducerDialogSavedTemplatesSelector,
                sagasDialogSavedTemplatesSelector,
                reducer,
                sagas,
                component,
              ]) => {
                reducerInjectors.injectReducer('dialogPatientMerge', reducerDialogPatientMerge.default);
                sagaInjectors.injectSaga('containers/DialogPatientMerge/sagas', {
                  saga: sagasDialogPatientMerge.default,
                });
                reducerInjectors.injectReducer(
                  'dialogSavedTemplatesSelector',
                  reducerDialogSavedTemplatesSelector.default,
                );
                sagaInjectors.injectSaga('containers/DialogSavedTemplatesSelector/sagas', {
                  saga: sagasDialogSavedTemplatesSelector.default,
                });
                reducerInjectors.injectReducer('patientPage', reducer.default);
                sagaInjectors.injectSaga('containers/PatientPage/sagas', { saga: sagas.default });
                renderRoute(component);
              },
            );

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/patients/:id/formAnswers/:formId',
          name: 'patientFormAnswersPage',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/PatientIntakeFormAnswersPage/sagas'),
              import('containers/PatientIntakeFormAnswersPage'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([sagas, component]) => {
              sagaInjectors.injectSaga('containers/PatientIntakeFormAnswersPage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/patients/:id/formAnswers/:formId/review',
          getComponent(_, cb) {
            import('screens/PatientFormAnswersReviewScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/patients/:id/formAnswers/:formId/print',
          name: 'patientFormAnswersPrintPage',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/PatientIntakeFormAnswersPage/sagas'),
              import('containers/PatientIntakeFormAnswersPage/indexPrint'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([sagas, component]) => {
              sagaInjectors.injectSaga('containers/PatientIntakeFormAnswersPage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/faxes',
          name: 'faxes',
          onEnter,
          getComponent(_, cb) {
            import('screens/nexty-next/FaxAutomation')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/faxes/:id',
          name: 'faxDetails',
          onEnter,
          getComponent(_, cb) {
            import('screens/nexty-next/FaxDetails')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/referrals',
          name: 'referrals',
          onEnter: () => browserHistory.push('/inbound-referrals'),
        },
        {
          path: '/inbound-referrals',
          name: 'referrals',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('modules/Referrals/pages/ReferralsList/containers/reducer'),
              import('modules/Referrals/pages/ReferralsList/containers/sagas'),
              import('screens/nexty-next/ReferralsInbound'),
            ]);
            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('referralsPage', reducer.default);
              sagaInjectors.injectSaga('modules/Referrals/pages/ReferralsList/containers/sagas', {
                saga: sagas.default,
              });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/outreach',
          name: 'outreach',
          onEnter,
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              import('modules/Referrals/pages/ReferralsList/containers/reducer'),
              import('modules/Referrals/pages/ReferralsList/containers/sagas'),
              import('modules/Referrals/pages/ReferralsList/wrappers/ReferralsQueryWrapper'),
            ]);
            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('referralsPage', reducer.default);
              sagaInjectors.injectSaga('modules/Referrals/pages/ReferralsList/containers/sagas', {
                saga: sagas.default,
              });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/outbound-referrals',
          name: 'outbound-referrals',
          onEnter,
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              import('modules/Referrals/pages/ReferralsList/containers/reducer'),
              import('modules/Referrals/pages/ReferralsList/containers/sagas'),
              import('modules/Referrals/pages/ReferralsList/wrappers/ReferralsQueryWrapper'),
            ]);
            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('referralsPage', reducer.default);
              sagaInjectors.injectSaga('modules/Referrals/pages/ReferralsList/containers/sagas', {
                saga: sagas.default,
              });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/referrals/upload',
          name: 'referrals',
          onEnter: () => browserHistory.push('/inbound-referrals/upload'),
        },
        {
          path: '/inbound-referrals/upload',
          name: 'referralsUpload',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/UploadReferralsPage/reducer'),
              import('containers/UploadReferralsPage/sagas'),
              import('containers/UploadReferralsPage'),
            ]);
            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('uploadReferralsPage', reducer.default);
              sagaInjectors.injectSaga('containers/UploadReferralsPage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/referral/outbound/:id',
          name: 'referralOutbound',
          onEnter: redirectURL('/outbound-referrals/:id'),
        },
        {
          path: '/outbound-referrals/:id',
          name: 'referralOutbound',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('modules/Referrals/pages/ReferralsForm/OutboundReferralsForm/containers/reducer'),
              import('modules/Referrals/pages/ReferralsForm/OutboundReferralsForm/containers/sagas'),
              import('modules/Referrals/pages/ReferralsForm/OutboundReferralsForm'),
              import('containers/DialogNewExternalProvider/reducer'),
              import('containers/DialogNewExternalProvider/sagas'),
            ]);
            const renderRoute = loadModule(cb);

            importModules.then(
              ([reducer, sagas, component, reducerDialogNewExternalProvider, sagasDialogNewExternalProvider]) => {
                reducerInjectors.injectReducer('referralOutboundPage', reducer.default);
                sagaInjectors.injectSaga(
                  'modules/Referrals/pages/ReferralsForm/OutboundReferralsForm/containers/sagas',
                  { saga: sagas.default },
                );
                reducerInjectors.injectReducer('dialogNewExternalProvider', reducerDialogNewExternalProvider.default);
                sagaInjectors.injectSaga('containers/DialogNewExternalProvider/sagas', {
                  saga: sagasDialogNewExternalProvider.default,
                });
                renderRoute(component);
              },
            );

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/referral/:id',
          name: 'referralPage',
          onEnter: redirectURL('/inbound-referrals/:id'),
        },
        {
          path: '/inbound-referrals/:id',
          name: 'referralPage',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('modules/Referrals/pages/ReferralsForm/InboundReferralsForm/containers/reducer'),
              import('modules/Referrals/pages/ReferralsForm/InboundReferralsForm/containers/sagas'),
              import('screens/nexty-next/FormReferralsInbound'),
              import('containers/DialogNewExternalProvider/reducer'),
              import('containers/DialogNewExternalProvider/sagas'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(
              ([reducer, sagas, component, reducerDialogNewExternalProvider, sagasDialogNewExternalProvider]) => {
                reducerInjectors.injectReducer('referralPage', reducer.default);
                sagaInjectors.injectSaga(
                  'modules/Referrals/pages/ReferralsForm/InboundReferralsForm/containers/sagas',
                  { saga: sagas.default },
                );
                reducerInjectors.injectReducer('dialogNewExternalProvider', reducerDialogNewExternalProvider.default);
                sagaInjectors.injectSaga('containers/DialogNewExternalProvider/sagas', {
                  saga: sagasDialogNewExternalProvider.default,
                });
                renderRoute(component);
              },
            );

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/hub',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/DialogSavedTemplatesSelector/reducer'),
              import('containers/DialogSavedTemplatesSelector/sagas'),
              import('screens/nexty-next/Hub'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(
              ([reducerDialogSavedTemplatesSelector, sagasDialogSavedTemplatesSelector, component]) => {
                reducerInjectors.injectReducer(
                  'dialogSavedTemplatesSelector',
                  reducerDialogSavedTemplatesSelector.default,
                );
                sagaInjectors.injectSaga('containers/DialogSavedTemplatesSelector/sagas', {
                  saga: sagasDialogSavedTemplatesSelector.default,
                });
                renderRoute(component);
              },
            );

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/admin',
          name: 'adminPage',
          onEnter,
          getComponent(_, cb) {
            import('containers/AdminPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/uploadcenter',
          onEnter,
          getComponent(_, cb) {
            import('containers/UploadCenterPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/waiting-room',
          getComponent(_, cb) {
            import('screens/nexty-next/WaitingRoom')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/waitlists',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/WaitlistPage/reducer'),
              import('containers/WaitlistPage/sagas'),
              import('containers/DialogSendOffers/reducer'),
              import('containers/DialogSendOffers/sagas'),
              import('screens/nexty-next/Waitlists'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(
              ([reducerWaitlistPage, sagasWaitlistPage, reducerDialogSendOffers, sagasDialogSendOffers, component]) => {
                reducerInjectors.injectReducer('waitlistPage', reducerWaitlistPage.default);
                sagaInjectors.injectSaga('containers/WaitlistPage/sagas', { saga: sagasWaitlistPage.default });
                reducerInjectors.injectReducer('dialogSendOffer', reducerDialogSendOffers.default);
                sagaInjectors.injectSaga('containers/DialogSendOffers/sagas', { saga: sagasDialogSendOffers.default });
                renderRoute(component);
              },
            );

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/waitlists/:id',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/WaitlistPage/reducer'),
              import('containers/WaitlistPage/sagas'),
              import('containers/WaitlistPage'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('waitlistPage', reducer.default);
              sagaInjectors.injectSaga('containers/WaitlistPage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/waitlists/:id',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/WaitlistPage/reducer'),
              import('containers/WaitlistPage/sagas'),
              import('containers/WaitlistPage'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('waitlistPage', reducer.default);
              sagaInjectors.injectSaga('containers/WaitlistPage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/file-management',
          getComponent(_, cb) {
            import('screens/FileManagementScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/form-responses',
          getComponent(_, cb) {
            import('screens/FormResponsesScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/followup-workflows',
          getComponent(_, cb) {
            import('screens/FollowUpWorkflowsScreen/List')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/followup-workflows/new',
          getComponent(_, cb) {
            import('screens/FollowUpWorkflowsScreen/CreateAndEdit')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/followup-workflows/:id',
          getComponent(_, cb) {
            import('screens/FollowUpWorkflowsScreen/CreateAndEdit')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/home',
          getComponent(_, cb) {
            import('screens/MissionControl')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/home/my-account',
          getComponent(_, cb) {
            Promise.all([
              import('screens/MissionControl/MyAccountPage'),
              import('containers/DialogTwoFactorDisable/reducer'),
              import('containers/DialogTwoFactorDisable/sagas'),
              import('containers/DialogTwoFactorEnable/reducer'),
              import('containers/DialogTwoFactorEnable/sagas'),
            ])
              .then(
                ([
                  component,
                  dialogTwoFactorDisableReducer,
                  dialogTwoFactorDisableSagas,
                  dialogTwoFactorEnableReducer,
                  dialogTwoFactorEnableSagas,
                ]) => {
                  reducerInjectors.injectReducer('dialogTwoFactorDisable', dialogTwoFactorDisableReducer.default);
                  sagaInjectors.injectSaga('containers/DialogTwoFactorDisable/sagas', {
                    saga: dialogTwoFactorDisableSagas.default,
                  });
                  reducerInjectors.injectReducer('dialogTwoFactorEnable', dialogTwoFactorEnableReducer.default);
                  sagaInjectors.injectSaga('containers/DialogTwoFactorEnable/sagas', {
                    saga: dialogTwoFactorEnableSagas.default,
                  });
                  loadModule(cb)(component);
                },
              )
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/home/main-account',
          getComponent(_, cb) {
            import('screens/MissionControl/MainAccountPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/ehr-integration/overview(/:id)',
          getComponent(_, cb) {
            import('screens/MissionControl/EHRIntegrationPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/ehr-integration/:resource(?:tab)',
          getComponent(_, cb) {
            import('modules/MissionControl/modules/SystemHealth/SystemHealthPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/integration-health/overview',
          getComponent(_, cb) {
            import('modules/MissionControl/modules/SystemHealth/SystemHealthOverviewPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/integration-health/:resource(?:tab)',
          getComponent(_, cb) {
            import('modules/MissionControl/modules/SystemHealth/SystemHealthPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/features/:feature',
          getComponent(_, cb) {
            import('screens/MissionControl/FeaturesSettingsPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/home/main-account/supported-products',
          getComponent(_, cb) {
            import('screens/MissionControl/MainAccountPage/SupportedProductsPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/security-center/users',
          getComponent(_, cb) {
            import('screens/MissionControl/SecurityCenterPage/UsersPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/security-center/groups',
          getComponent(_, cb) {
            import('screens/MissionControl/SecurityCenterPage/Groups/GroupsPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/security-center/multiple-account-management',
          getComponent(_, cb) {
            import('screens/MissionControl/SecurityCenterPage/MultipleAccountManagement')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/security-center/tokens',
          getComponent(_, cb) {
            import('screens/MissionControl/SecurityCenterPage/TokensPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/security-center/groups/create-new-group',
          getComponent(_, cb) {
            import('screens/MissionControl/SecurityCenterPage/Groups/CreateGroupPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/security-center/groups/:id',
          getComponent(_, cb) {
            import('screens/MissionControl/SecurityCenterPage/Groups/EditGroupPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/security-center/groups/:id/facilities',
          getComponent(_, cb) {
            import('screens/MissionControl/SecurityCenterPage/Groups/GroupFacilitiesPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/security-center/groups/:id/users',
          getComponent(_, cb) {
            import('screens/MissionControl/SecurityCenterPage/Groups/GroupUsersPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/security-center/users/add-new-user',
          getComponent(_, cb) {
            import('screens/MissionControl/SecurityCenterPage/AddUsersPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/security-center/users/give-user-access',
          getComponent(_, cb) {
            import('screens/MissionControl/SecurityCenterPage/GiveUserAccess')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/security-center/users/:id',
          getComponent(_, cb) {
            import('screens/MissionControl/SecurityCenterPage/EditUsersPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/security-center/users/:id/groups',
          getComponent(_, cb) {
            import('screens/MissionControl/SecurityCenterPage/UserGroupsPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/v2/settings/utilities/availabilities-viewer',
          getComponent(_, cb) {
            import('screens/MissionControl/Utilities/AvailabilitiesViewerPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/v2/settings/utilities/eligibility-setup',
          getComponent(_, cb) {
            import('screens/nexty-next/EligibilitySetup')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/branding/patient-app',
          getComponent(_, cb) {
            Promise.all([
              import('screens/MissionControl/Utilities/BrandingPage/reducer.js'),
              import('screens/MissionControl/Utilities/BrandingPage/sagas.js'),
              import('screens/MissionControl/Utilities/BrandingPage'),
            ])
              .then(([reducer, sagas, component]) => {
                reducerInjectors.injectReducer('settingsStylingPage', reducer.default);
                sagaInjectors.injectSaga('containers/SettingsPage/SettingsStylingPage/sagas', { saga: sagas.default });
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/branding/patient-app-refresh',
          getComponent(_, cb) {
            import('screens/nexty-next/ContainerCustomWebStylesBuilder')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/branding/host-patient',
          getComponent(_, cb) {
            import('screens/nexty-next/ContainerCustomWebStylesBuilder')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/branding/cobranding',
          getComponent(_, cb) {
            import('screens/MissionControl/Utilities/Branding/Cobranding')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/branding/embeds',
          getComponent(_, cb) {
            import('screens/MissionControl/Utilities/Branding/EmbedBranding')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/spark-management/fax-transform-automations',
          getComponent(_, cb) {
            import('screens/MissionControl/SparkManagement/FaxTransformAutomations')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/spark-management/fax-transform-automations/document-type-management',
          getComponent(_, cb) {
            import('screens/MissionControl/SparkManagement/FaxDocumentTypeManagement')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/spark-management/fax-transform-automations/document-type-management/:id',
          getComponent(_, cb) {
            import('screens/MissionControl/SparkManagement/FaxDocumentTypeManagementForm')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/spark-management/fax-transform-automations/:id',
          getComponent(_, cb) {
            import('screens/MissionControl/SparkManagement/FaxTransformAutomationsForm')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/spark-management/fax-transform-automations/ehr-assignees/:id',
          getComponent(_, cb) {
            import('screens/MissionControl/SparkManagement/EhrAssigneesForm')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/spark-management/assistants',
          getComponent(_, cb) {
            import('screens/MissionControl/SparkManagement/Assistants')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/spark-management/assistants/:id',
          getComponent(_, cb) {
            import('screens/MissionControl/SparkManagement/AssistantsBuilder')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/checklist-builder',
          getComponent(_, cb) {
            import('screens/MissionControl/Utilities/ChecklistBuilder')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/checklist-builder/:id',
          getComponent(_, cb) {
            import('screens/MissionControl/Utilities/ChecklistBuilderForm')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/queue-manager-builder(/:id)',
          getComponent(_, cb) {
            import('screens/MissionControl/Utilities/QueueManagerBuilder')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/message-template-partials',
          getComponent(_, cb) {
            import('screens/MissionControl/Utilities/MessagePartials')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/message-template-partials/add',
          getComponent(_, cb) {
            import('screens/MissionControl/Utilities/MessagePartials/Create')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/message-template-partials/:id',
          getComponent(_, cb) {
            import('screens/MissionControl/Utilities/MessagePartials/Show')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/outbound-numbers',
          getComponent(_, cb) {
            import('screens/MissionControl/Utilities/OutboundNumbers/List')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/outbound-numbers/:id',
          getComponent(_, cb) {
            import('screens/MissionControl/Utilities/OutboundNumbers/Show')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/form-builder',
          getComponent(_, cb) {
            Promise.all([import('screens/MissionControl/Utilities/NewFormsListPage')])
              .then(([component]) => {
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/form-builder/:id',
          getComponent(_, cb) {
            Promise.all([import('screens/MissionControl/Utilities/NewFormBuilderPage')])
              .then(([component]) => {
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/patient-billing/items',
          getComponent(_, cb) {
            Promise.all([import('screens/MissionControl/Utilities/PatientBillingPage/components/BillingItems')])
              .then(([component]) => {
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/patient-billing/items/:id',
          getComponent(_, cb) {
            Promise.all([import('screens/MissionControl/Utilities/PatientBillingPage/components/BillingItems')])
              .then(([component]) => {
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/patient-billing/payouts',
          getComponent(_, cb) {
            Promise.all([import('screens/MissionControl/Utilities/PatientBillingPage/components/Payouts')])
              .then(([component]) => {
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/patient-billing/lumapay-settings',
          getComponent(_, cb) {
            Promise.all([import('screens/MissionControl/Utilities/PatientBillingPage/components/AccountManagement')])
              .then(([component]) => {
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/third-party-integrations',
          getComponent(_, cb) {
            Promise.all([import('screens/MissionControl/Utilities/ThirdPartyIntegrations')])
              .then(([component]) => {
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/third-party-integrations/ehr-integration/:vendor',
          getComponent(_, cb) {
            Promise.all([import('screens/MissionControl/Utilities/ThirdPartyIntegrations/ShowEHRIntegration')])
              .then(([component]) => loadModule(cb)(component))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/third-party-integrations/eligibility/:vendor',
          getComponent(_, cb) {
            import('screens/MissionControl/Utilities/ThirdPartyIntegrations/ShowEligibilityIntegration')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/third-party-integrations/forms/:vendor',
          getComponent(_, cb) {
            Promise.all([import('screens/MissionControl/Utilities/ThirdPartyIntegrations/ShowFormsIntegration')])
              .then(([component]) => loadModule(cb)(component))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/third-party-integrations/payment/:vendor',
          getComponent(_, cb) {
            Promise.all([import('screens/MissionControl/Utilities/ThirdPartyIntegrations/ShowPaymentsIntegration')])
              .then(([component]) => loadModule(cb)(component))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/third-party-integrations/reporting/:vendor',
          getComponent(_, cb) {
            Promise.all([import('screens/MissionControl/Utilities/ThirdPartyIntegrations/ShowReportingIntegration')])
              .then(([component]) => loadModule(cb)(component))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/third-party-integrations/insurance-verification/:vendor',
          getComponent(_, cb) {
            Promise.all([import('screens/MissionControl/Utilities/ThirdPartyIntegrations/ShowReportingIntegration')])
              .then(([component]) => loadModule(cb)(component))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/third-party-integrations/telehealth/:vendor',
          getComponent(_, cb) {
            Promise.all([import('screens/MissionControl/Utilities/ThirdPartyIntegrations/ShowTelehealthIntegration')])
              .then(([component]) => loadModule(cb)(component))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/third-party-integrations/number-management/:vendor',
          getComponent(_, cb) {
            Promise.all([import('screens/MissionControl/Utilities/ThirdPartyIntegrations/ShowMessageIntegration')])
              .then(([component]) => loadModule(cb)(component))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/third-party-integrations/:category',
          getComponent(_, cb) {
            Promise.all([
              import('screens/MissionControl/Utilities/ThirdPartyIntegrations/ThirdPartyCategoriesRedirect'),
            ])
              .then(([component]) => loadModule(cb)(component))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/third-party-integrations/redirect/:vendor',
          getComponent(_, cb) {
            Promise.all([
              import('screens/MissionControl/Utilities/ThirdPartyIntegrations/ThirdPartyIntegrationsRedirect.tsx'),
            ])
              .then(([component]) => {
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/message-library/broadcast',
          getComponent(_, cb) {
            import('screens/MissionControl/Utilities/MessageLibrary/BroadcastMessageLibrary/pages/List')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/message-library/broadcast/:id',
          getComponent(_, cb) {
            import('screens/MissionControl/Utilities/MessageLibrary/BroadcastMessageLibrary/pages/Form')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          // NOTE: [line below] 'someRoute(/:subRoute)' is react-router's (v1-v3) syntax for optional sub-routes
          path: 'v2/settings/utilities/message-library(/:tab)',
          getComponent(_, cb) {
            Promise.all([
              import('screens/MissionControl/Utilities/MessageLibraryPage/LibraryPage/reducer.js'),
              import('screens/MissionControl/Utilities/MessageLibraryPage/LibraryPage/sagas.js'),
              import('screens/MissionControl/Utilities/MessageLibraryPage'),
            ])
              .then(([reducer, sagas, component]) => {
                reducerInjectors.injectReducer('settingsLibraryPage', reducer.default);
                sagaInjectors.injectSaga('screens/MissionControl/Utilities/MessageLibraryPage/LibraryPage/sagas', {
                  saga: sagas.default,
                });
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          // NOTE: [line below] 'someRoute(/:subRoute)' is react-router's (v1-v3) syntax for optional sub-routes
          path: 'v2/settings/utilities/message-templates(/:tab)(/:id)',
          getComponent(_, cb) {
            Promise.all([
              import('screens/MissionControl/Utilities/MessageTemplatesPage/TemplatesPage/reducer.js'),
              import('screens/MissionControl/Utilities/MessageTemplatesPage/TemplatesPage/sagas.js'),
              import('screens/MissionControl/Utilities/MessageTemplatesPage'),
            ])
              .then(([reducer, sagas, component]) => {
                reducerInjectors.injectReducer('settingsTemplatesPage', reducer.default);
                sagaInjectors.injectSaga('screens/MissionControl/Utilities/MessageTemplatesPage/TemplatesPage/sagas', {
                  saga: sagas.default,
                });
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/(:variant)-builder',
          getComponent(_, cb) {
            Promise.all([
              import('screens/MissionControl/Utilities/FormBuilderPage/reducer.js'),
              import('screens/MissionControl/Utilities/FormBuilderPage/sagas.js'),
              import('screens/MissionControl/Utilities/FormBuilderPage/LumaBotBuilderListVersioning'),
            ])
              .then(([reducer, sagas, component]) => {
                reducerInjectors.injectReducer('formBuilder', reducer.default);
                sagaInjectors.injectSaga('formBuilder/sagas', { saga: sagas.default });
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/(:variant)-builder/:id',
          getComponent(_, cb) {
            Promise.all([
              import('screens/MissionControl/Utilities/FormBuilderPage/reducer.js'),
              import('screens/MissionControl/Utilities/FormBuilderPage/sagas.js'),
              import('screens/MissionControl/Utilities/FormBuilderPage/LumaBotBuilderVersioning'),
            ])
              .then(([reducer, sagas, component]) => {
                reducerInjectors.injectReducer('formBuilder', reducer.default);
                sagaInjectors.injectSaga('formBuilder/sagas', { saga: sagas.default });
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: '/patients/:patientId/lumabot-session/:flowId',
          name: 'patientFlowSessionScreen',
          getComponent(_, cb) {
            import('screens/LumaBotSessionScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/patients/:patientId/lumabot-session/:flowId/print',
          name: 'patientFlowSessionScreen',
          getComponent(_, cb) {
            import('screens/LumaBotSessionScreen/print')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/custom-actions',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/SettingsCustomActionsPage/reducer'),
              import('containers/SettingsCustomActionsPage/sagas'),
              import('screens/MissionControl/Utilities/CustomActionsPage'),
              import('containers/DialogCustomActionGroup/reducer'),
              import('containers/DialogCustomActionGroup/sagas'),
              import('containers/DialogDeleteCustomAction/reducer'),
              import('containers/DialogDeleteCustomAction/sagas'),
              import('containers/DialogDeleteCustomActionGroup/reducer'),
              import('containers/DialogDeleteCustomActionGroup/sagas'),
              import('containers/DialogMoveCustomAction/reducer'),
              import('containers/DialogMoveCustomAction/sagas'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(
              ([
                reducer,
                sagas,
                component,
                dialogCustomActionGroupReducer,
                dialogCustomActionGroupSagas,
                dialogDeleteCustomActionReducer,
                dialogDeleteCustomActionSagas,
                dialogDeleteCustomActionGroupReducer,
                dialogDeleteCustomActionGroupSagas,
                dialogMoveCustomActionReducer,
                dialogMoveCustomActionSagas,
              ]) => {
                reducerInjectors.injectReducer('settingsCustomActionsPage', reducer.default);
                sagaInjectors.injectSaga('containers/CustomActionsPage/sagas', { saga: sagas.default });
                reducerInjectors.injectReducer('dialogCustomActionGroup', dialogCustomActionGroupReducer.default);
                sagaInjectors.injectSaga('containers/DialogCustomActionGroup/sagas', {
                  saga: dialogCustomActionGroupSagas.default,
                });
                reducerInjectors.injectReducer('dialogDeleteCustomAction', dialogDeleteCustomActionReducer.default);
                sagaInjectors.injectSaga('containers/DialogDeleteCustomAction/sagas', {
                  saga: dialogDeleteCustomActionSagas.default,
                });
                reducerInjectors.injectReducer(
                  'dialogDeleteCustomActionGroup',
                  dialogDeleteCustomActionGroupReducer.default,
                );
                sagaInjectors.injectSaga('DialogDeleteCustomActionGroup', {
                  saga: dialogDeleteCustomActionGroupSagas.default,
                });
                reducerInjectors.injectReducer('dialogMoveCustomAction', dialogMoveCustomActionReducer.default);
                sagaInjectors.injectSaga('containers/DialogMoveCustomAction/sagas', {
                  saga: dialogMoveCustomActionSagas.default,
                });
                renderRoute(component);
              },
            );

            importModules.catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/custom-actions/appointment/:id',
          name: 'settingsCustomActionNewAppointmentPage',
          onEnter,
          getComponent(_, cb) {
            Promise.all([
              import('containers/SettingsCustomActionNewAppointmentPage/reducer'),
              import('containers/SettingsCustomActionNewAppointmentPage/sagas'),
              import('screens/MissionControl/Utilities/CustomActionsPage/NewAppointmentPage'),
            ])
              .then(([reducer, sagas, component]) => {
                reducerInjectors.injectReducer('settingsCustomActionNewAppointmentPage', reducer.default);
                sagaInjectors.injectSaga('containers/settingsCustomActionNewAppointmentPage/sagas', {
                  saga: sagas.default,
                });
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/custom-actions/diagnostic/:id',
          name: 'settingsCustomActionNewDiagnosticPage',
          onEnter,
          getComponent(_, cb) {
            Promise.all([
              import('containers/SettingsCustomActionNewDiagnosticPage/reducer'),
              import('containers/SettingsCustomActionNewDiagnosticPage/sagas'),
              import('screens/MissionControl/Utilities/CustomActionsPage/NewDiagnosticPage'),
            ])
              .then(([reducer, sagas, component]) => {
                reducerInjectors.injectReducer('settingsCustomActionNewDiagnosticPage', reducer.default);
                sagaInjectors.injectSaga('containers/settingsCustomActionNewDiagnosticPage/sagas', {
                  saga: sagas.default,
                });
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/custom-actions/reminder/:id',
          name: 'settingsCustomActionNewExtraReminderPage',
          onEnter,
          getComponent(_, cb) {
            Promise.all([
              import('containers/SettingsCustomActionNewExtraReminderPage/reducer'),
              import('containers/SettingsCustomActionNewExtraReminderPage/sagas'),
              import('screens/MissionControl/Utilities/CustomActionsPage/NewExtraReminderPage'),
            ])
              .then(([reducer, sagas, component]) => {
                reducerInjectors.injectReducer('settingsCustomActionNewExtraReminderPage', reducer.default);
                sagaInjectors.injectSaga('containers/settingsCustomActionNewExtraReminderPage/sagas', {
                  saga: sagas.default,
                });
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/custom-actions/procedure/:id',
          name: 'settingsCustomActionNewProcedurePage',
          onEnter,
          getComponent(_, cb) {
            Promise.all([
              import('containers/SettingsCustomActionNewProcedurePage/reducer'),
              import('containers/SettingsCustomActionNewProcedurePage/sagas'),
              import('screens/MissionControl/Utilities/CustomActionsPage/NewProcedurePage'),
            ])
              .then(([reducer, sagas, component]) => {
                reducerInjectors.injectReducer('settingsCustomActionNewProcedurePage', reducer.default);
                sagaInjectors.injectSaga('containers/settingsCustomActionNewProcedurePage/sagas', {
                  saga: sagas.default,
                });
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/custom-actions/recall/:id',
          name: 'settingsCustomActionNewRecallPage',
          onEnter,
          getComponent(_, cb) {
            Promise.all([
              import('containers/SettingsCustomActionNewRecallPage/reducer'),
              import('containers/SettingsCustomActionNewRecallPage/sagas'),
              import('screens/MissionControl/Utilities/CustomActionsPage/NewRecallPage'),
            ])
              .then(([reducer, sagas, component]) => {
                reducerInjectors.injectReducer('settingsCustomActionNewRecallPage', reducer.default);
                sagaInjectors.injectSaga('containers/settingsCustomActionNewRecallPage/sagas', {
                  saga: sagas.default,
                });
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/practice-resources/providers',
          getComponent(_, cb) {
            import('screens/MissionControl/PracticeResourcesPage/ProviderResourcePage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/practice-resources/providers/:id',
          getComponent(_, cb) {
            Promise.all([
              import('containers/DialogAvailabilityBlocks/reducer'),
              import(
                // eslint-disable-next-line
                'screens/MissionControl/PracticeResourcesPage/ProviderResourcePage/ProviderEditPage'
              ),
            ])
              .then(([reducer, component]) => {
                reducerInjectors.injectReducer('dialogAvailabilityBlocks', reducer.default);
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/practice-resources/facilities',
          getComponent(_, cb) {
            import('screens/MissionControl/PracticeResourcesPage/FacilityResourcePage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/practice-resources/facilities/:id',
          getComponent(_, cb) {
            import('screens/MissionControl/PracticeResourcesPage/FacilityResourcePage/FacilityEditPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/practice-resources/specialties',
          getComponent(_, cb) {
            import('screens/MissionControl/PracticeResourcesPage/SpecialtyResourcePage/List')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/practice-resources/specialties/:id',
          getComponent(_, cb) {
            import('screens/MissionControl/PracticeResourcesPage/SpecialtyResourcePage/Form')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/practice-resources/appointment-types',
          getComponent(_, cb) {
            import('screens/MissionControl/PracticeResourcesPage/ApptTypeResourcePage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/practice-resources/appointment-types/:id',
          getComponent(_, cb) {
            Promise.all([
              import('containers/DialogAvailabilityBlocks/reducer'),
              import(
                // eslint-disable-next-line
                'screens/MissionControl/PracticeResourcesPage/ApptTypeResourcePage/ApptTypeEditPage'
              ),
            ])
              .then(([reducer, component]) => {
                reducerInjectors.injectReducer('dialogAvailabilityBlocks', reducer.default);
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/followup-workflows',
          getComponent(_, cb) {
            import('screens/nexty-next/FollowupWorkflows')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/followup-workflows/builder',
          getComponent(_, cb) {
            import('screens/nexty-next/FollowupWorkflowBuilder')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/broadcast-templates',
          getComponent(_, cb) {
            import('screens/nexty-next/BroadcastTemplates')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: 'v2/settings/utilities/broadcast-templates/:id',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('modules/Broadcasts/pages/BroadcastsForm'),
              // TODO Refactor DialogSavedTemplatesSelector to skip using redux-saga
              import('containers/DialogSavedTemplatesSelector/reducer'),
              import('containers/DialogSavedTemplatesSelector/sagas'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(
              ([component, reducerDialogSavedTemplatesSelector, sagasDialogSavedTemplatesSelector]) => {
                reducerInjectors.injectReducer(
                  'dialogSavedTemplatesSelector',
                  reducerDialogSavedTemplatesSelector.default,
                );
                sagaInjectors.injectSaga('containers/DialogSavedTemplatesSelector/sagas', {
                  saga: sagasDialogSavedTemplatesSelector.default,
                });
                renderRoute(component);
              },
            );

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/reports/:tab',
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/FeaturedReports')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/reports/:tab/:id',
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/FeaturedReports')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/analyzer',
          getComponent(_, cb) {
            import('screens/PracticeAnalyzerScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/offers(/:id)',
          name: 'offers',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/OffersPage/reducer'),
              import('containers/OffersPage/sagas'),
              import('screens/Offers'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('offersPage', reducer.default);
              sagaInjectors.injectSaga('containers/OffersPage/sagas', { saga: sagas.default });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/settings/actions',
          name: 'settingsCustomActions',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/SettingsCustomActionsPage/reducer'),
              import('containers/SettingsCustomActionsPage/sagas'),
              import('containers/SettingsCustomActionsPage'),
              import('containers/DialogCustomActionGroup/reducer'),
              import('containers/DialogCustomActionGroup/sagas'),
              import('containers/DialogDeleteCustomAction/reducer'),
              import('containers/DialogDeleteCustomAction/sagas'),
              import('containers/DialogDeleteCustomActionGroup/reducer'),
              import('containers/DialogDeleteCustomActionGroup/sagas'),
              import('containers/DialogMoveCustomAction/reducer'),
              import('containers/DialogMoveCustomAction/sagas'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(
              ([
                reducer,
                sagas,
                component,
                dialogCustomActionGroupReducer,
                dialogCustomActionGroupSagas,
                dialogDeleteCustomActionReducer,
                dialogDeleteCustomActionSagas,
                dialogDeleteCustomActionGroupReducer,
                dialogDeleteCustomActionGroupSagas,
                dialogMoveCustomActionReducer,
                dialogMoveCustomActionSagas,
              ]) => {
                reducerInjectors.injectReducer('settingsCustomActionsPage', reducer.default);
                sagaInjectors.injectSaga('containers/CustomActionsPage/sagas', { saga: sagas.default });
                reducerInjectors.injectReducer('dialogCustomActionGroup', dialogCustomActionGroupReducer.default);
                sagaInjectors.injectSaga('containers/DialogCustomActionGroup/sagas', {
                  saga: dialogCustomActionGroupSagas.default,
                });
                reducerInjectors.injectReducer('dialogDeleteCustomAction', dialogDeleteCustomActionReducer.default);
                sagaInjectors.injectSaga('containers/DialogDeleteCustomAction/sagas', {
                  saga: dialogDeleteCustomActionSagas.default,
                });
                reducerInjectors.injectReducer(
                  'dialogDeleteCustomActionGroup',
                  dialogDeleteCustomActionGroupReducer.default,
                );
                sagaInjectors.injectSaga('DialogDeleteCustomActionGroup', {
                  saga: dialogDeleteCustomActionGroupSagas.default,
                });
                reducerInjectors.injectReducer('dialogMoveCustomAction', dialogMoveCustomActionReducer.default);
                sagaInjectors.injectSaga('containers/DialogMoveCustomAction/sagas', {
                  saga: dialogMoveCustomActionSagas.default,
                });
                renderRoute(component);
              },
            );

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/settings/action/appointment/:id',
          name: 'settingsCustomActionNewAppointmentPage',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/SettingsCustomActionNewAppointmentPage/reducer'),
              import('containers/SettingsCustomActionNewAppointmentPage/sagas'),
              import('containers/SettingsCustomActionNewAppointmentPage/SettingsCustomActionNewAppointmentPageQuery'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('settingsCustomActionNewAppointmentPage', reducer.default);
              sagaInjectors.injectSaga('containers/SettingsCustomActionNewAppointmentPage/sagas', {
                saga: sagas.default,
              });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/settings/action/recall/:id',
          name: 'settingsCustomActionNewRecallPage',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/SettingsCustomActionNewRecallPage/reducer'),
              import('containers/SettingsCustomActionNewRecallPage/sagas'),
              import('containers/SettingsCustomActionNewRecallPage/SettingsCustomActionNewRecallPageQuery'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('settingsCustomActionNewRecallPage', reducer.default);
              sagaInjectors.injectSaga('containers/SettingsCustomActionNewRecallPage/sagas', {
                saga: sagas.default,
              });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/settings/action/diagnostic/:id',
          name: 'settingsCustomActionNewDiagnosticPage',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/SettingsCustomActionNewDiagnosticPage/reducer'),
              import('containers/SettingsCustomActionNewDiagnosticPage/sagas'),
              import('containers/SettingsCustomActionNewDiagnosticPage/SettingsCustomActionNewDiagnosticPageQuery'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('settingsCustomActionNewDiagnosticPage', reducer.default);
              sagaInjectors.injectSaga('containers/SettingsCustomActionNewDiagnosticPage/sagas', {
                saga: sagas.default,
              });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/settings/action/procedure/:id',
          name: 'settingsCustomActionNewProcedurePage',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/SettingsCustomActionNewProcedurePage/reducer'),
              import('containers/SettingsCustomActionNewProcedurePage/sagas'),
              import('containers/SettingsCustomActionNewProcedurePage/SettingsCustomActionNewProcedurePageQuery'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('settingsCustomActionNewProcedurePage', reducer.default);
              sagaInjectors.injectSaga('containers/SettingsCustomActionNewProcedurePage/sagas', {
                saga: sagas.default,
              });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/settings/action/reminder/:id',
          name: 'settingsCustomActionNewExtraReminderPage',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('containers/SettingsCustomActionNewExtraReminderPage/reducer'),
              import('containers/SettingsCustomActionNewExtraReminderPage/sagas'),
              import(
                // eslint-disable-next-line
                'containers/SettingsCustomActionNewExtraReminderPage/SettingsCustomActionNewExtraReminderPageQuery'
              ),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([reducer, sagas, component]) => {
              reducerInjectors.injectReducer('settingsCustomActionNewExtraReminderPage', reducer.default);
              sagaInjectors.injectSaga('containers/SettingsCustomActionNewExtraReminderPage/sagas', {
                saga: sagas.default,
              });
              renderRoute(component);
            });

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/patientBilling',
          onEnter: redirectURL('/lumapay/summary'),
        },
        {
          path: '/lumapay/summary',
          getComponent(_, cb) {
            Promise.all([import('screens/LumaPayScreen'), import('screens/PatientBillingScreen')])
              .then(([component]) => {
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: '/settings',
          name: 'settings',
          onEnter,
          childRoutes: [
            {
              path: '/settings/:page',
            },
            {
              path: '/settings/:page/:tab',
            },
            {
              path: '/settings/:page/:model/:id',
            },
          ],
        },
        {
          path: '/broadcasts',
          getComponent(_, cb) {
            import('screens/nexty-next/BroadcastList')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/broadcasts/:id(/:action)',
          onEnter,
          getComponent(_, cb) {
            const importModules = Promise.all([
              import('screens/nexty-next/BroadcastFlows'),
              // TODO Refactor DialogSavedTemplatesSelector to skip using redux-saga
              import('containers/DialogSavedTemplatesSelector/reducer'),
              import('containers/DialogSavedTemplatesSelector/sagas'),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(
              ([component, reducerDialogSavedTemplatesSelector, sagasDialogSavedTemplatesSelector]) => {
                reducerInjectors.injectReducer(
                  'dialogSavedTemplatesSelector',
                  reducerDialogSavedTemplatesSelector.default,
                );
                sagaInjectors.injectSaga('containers/DialogSavedTemplatesSelector/sagas', {
                  saga: sagasDialogSavedTemplatesSelector.default,
                });
                renderRoute(component);
              },
            );

            importModules.catch(errorLoading);
          },
        },
        {
          path: '/salucro',
          getComponent(nextState, cb) {
            Promise.all([import('screens/SalucroScreen')])
              .then(([component]) => {
                loadModule(cb)(component);
              })
              .catch(errorLoading);
          },
        },
        {
          path: '/messaging-activity/:subTab',
          getComponent(_, cb) {
            import('./modules/InsightsCenter/pages/MessagingActivity')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/downloads/:id',
          getComponent(_, cb) {
            import('screens/DownloadScreen')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/labs/cancellation-prediction',
          getComponent(_, cb) {
            import('containers/Labs/CancellationPrediction')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/labs/order-ride',
          getComponent(_, cb) {
            import('containers/Labs/OrderRide')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/access-denied',
          getComponent(_, cb) {
            import('containers/AccessDeniedPage')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/recalls',
          getComponent(_, cb) {
            import('modules/Recalls/pages/RecallsList')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/followup-workflows(/:followupWorkflow)',
          getComponent(_, cb) {
            import('screens/nexty-next/FollowupWorkflowInstances')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        {
          path: '/queue-manager',
          getComponent(_, cb) {
            import('screens/nexty-next/QueueManager')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
        // Configure route for dev playground
        hasAccessDevPlayground() && {
          path: '/dev/:key',
          onEnter: (_, replace) => {
            if (!storage.getToken()) {
              return replace({ pathname: '/demo-setup' });
            }
          },
          getComponent(_, cb) {
            import('dev/DevPlayground')
              .then(loadModule(cb))
              .catch(errorLoading);
          },
        },
      ]),
    },
  ];

  internalRoutes.push({
    path: '/embed-error',
    name: 'embederror',
    getComponent(_, cb) {
      import('embeds/components/EmbedError/EmbedError')
        .then(loadModule(cb))
        .catch(errorLoading);
    },
  });

  internalRoutes.push({
    path: '*',
    name: 'notfound',
    getComponent(_, cb) {
      import('core/NotFoundPage')
        .then(loadModule(cb))
        .catch(errorLoading);
    },
  });

  return internalRoutes;
}
